/**
 * Subscription plan banner.
 *
 * This component displays the information of the subscription plan of the user
 * in a banner placed in top of the main screen.
 *
 * Copyright (c) Ircam Amplify 2024-2026
 */

import {
  AccountUtil,
  CompanyConsumption,
  TextUtil,
  TierType,
  Util,
} from '@amplify3/duplo-back-end'
import { BiCoin } from 'react-icons/bi'
import { Link } from 'react-router-dom'
import { getLayoutState, getUserState, useSelector } from '../store'
import './SubscriptionPlanBanner.css'

const SubscriptionPlanBanner = () => {
  const { userInfo, companyConsumption } = useSelector(getUserState)
  const { sideBarCollapsed } = useSelector(getLayoutState)

  if (!userInfo || !companyConsumption?.value.subscription_tier)
    return <div></div>

  /**
   * Return the plan name to display in the banner message.
   *
   * @param tier The tier.
   * @returns A string containing the name, or 'Unknown plan'
   *  if the tier is undefined.
   */
  function getPlanName(
    tier: string | undefined,
    subscription_name: string | undefined
  ): string {
    return (
      TextUtil.capitalizeWordFirstChar(
        AccountUtil.humanReadableTier(tier, subscription_name)
      ) + ' plan'
    )
  }

  /**
   * The component displaying the message with the name of the plan.
   */
  const PlanMessageComp = () => {
    return (
      <div>
        {companyConsumption?.value?.subscription_tier !== TierType.Free && (
          <div className="planMessage">
            You are using the{' '}
            <span className="planMessage-name">
              {getPlanName(
                companyConsumption?.value.subscription_tier,
                companyConsumption?.value.subscription_name
              )}
            </span>
          </div>
        )}
      </div>
    )
  }

  /**
   * Component displaying the consumed credit with prefix and suffix messages
   * depending on the tier.
   */
  const CreditCounter = ({
    consumption,
  }: {
    consumption: CompanyConsumption
  }) => {
    const consumedCount = AccountUtil.getConsumption(consumption)
    const creditCount = Util.absRound(
      TierType.Free == consumption?.subscription_tier
        ? consumedCount +
            AccountUtil.getPaidCreditCount(companyConsumption?.value)
        : consumedCount
    )

    // The unit
    const unit = creditCount > 0 ? ' credits ' : ' credit '

    // The suffix
    const getCreditSuffix = () => {
      switch (consumption?.subscription_tier) {
        case TierType.Free:
        case TierType.TopUpCreditAimd:
          return 'left'
        case TierType.Subscription:
          return (
            `/ ${consumption.current_plan} this ` +
            (consumption.current_subscription_frequency === 'monthly'
              ? 'month'
              : 'year')
          )
        case TierType.PayAsYouGo:
          return 'this month'
        default:
          return null
      }
    }
    const suffix = getCreditSuffix()

    return (
      <div className="creditCounter">
        <BiCoin size={25} className="creditCounter-icon" />
        {creditCount} {unit}
        {suffix}
      </div>
    )
  }

  interface ConsumptionInfoProps {
    consumption: CompanyConsumption
  }

  /**
   * Component displaying the information of the consumption, with link to
   * change the plan.
   */
  const ConsumptionInfoComp = ({ consumption }: ConsumptionInfoProps) => {
    const getLinkLabel = () => {
      switch (consumption?.subscription_tier) {
        case TierType.Free:
          return 'Upgrade now'
        case TierType.TopUpCreditAimd:
          return 'Buy Credits'
        case TierType.Subscription:
        case TierType.PayAsYouGo:
          return 'Browse plan'
        default:
          return null
      }
    }
    const linkLabel = getLinkLabel()

    const getPrefixMsg = () => {
      if (
        TierType.Free != consumption?.subscription_tier &&
        TierType.TopUpCreditAimd != consumption?.subscription_tier
      ) {
        return 'You have used'
      }

      return null
    }
    const prefixMsg = getPrefixMsg()

    return (
      <div className="consumptionInfo">
        {prefixMsg}

        <CreditCounter consumption={consumption} />

        {linkLabel && (
          <Link to="/tierselection" className="consumptionInfo-link">
            {linkLabel}
          </Link>
        )}
      </div>
    )
  }

  return (
    <div
      className={`subscriptionPlan${
        !sideBarCollapsed ? ' sidebar-expanded' : ''
      }`}
    >
      <PlanMessageComp />
      {AccountUtil.hasCreditLimit(companyConsumption?.value) && (
        <ConsumptionInfoComp consumption={companyConsumption?.value} />
      )}
    </div>
  )
}

export default SubscriptionPlanBanner
